import { snapshot, subscribe } from 'valtio'
import { intl } from 'lang/intl'
import { subscribeKey } from 'valtio/utils'

import { mutingState, setMutingIntention } from '../../firebase/hooks/useMute'
import { trayState } from '../../firebase/hooks/useTray'
import { showNotice } from '../../Layout/Notices/Notices'
import { logMessage } from '../../Logging/log'
import { roomState, setTrackState } from '../webRTCRoom'
import isHeadless from '../../headless/isHeadless'
import { playbackState } from '../../firebase/hooks/useVideoPlayback'
import {
  floorState,
  isParticipantScreenAloneInFloor,
  isParticipantScreenInFloor,
} from '../../firebase/hooks/useFloor'
import { panelState } from '../../firebase/hooks/usePanel'

// all muting happens through a firebase muting intention
const toggleParticipantAudio = async (participantId: number) => {
  const participant = roomState.participants.find((p) => p.id === participantId)

  if (!participant) {
    logMessage('Attempted to unmute non-existent participant', {
      level: 'warning',
      extra: {
        participantId,
      },
    })
    return
  }

  if (!participant.tracks.cameraAudio) {
    showNotice({
      message: intl.formatMessage({
        defaultMessage: "This speaker doesn't have a microphone configured",
        id: 'sFC/bh',
      }),
      expirationMillis: 2000,
    })
    logMessage('Attempted to unmute non-existent audio-track', {
      level: 'warning',
      extra: {
        participantId,
        tracks: snapshot(participant.tracks),
      },
    })
    return
  }

  await setMutingIntention({
    participantId,
    isMuted: participant.tracks.cameraAudio.isEnabled,
    // reset auto muting because we have human intent here, which has higher priority
    isPlaybackMuted: false,
  })
}

const enableParticipantAudio = (participantId: number) => {
  setMutingIntention({
    participantId,
    isMuted: false,
    isPlaybackMuted: false,
  })
}

const syncAudioStates = () => {
  const { isMuted, isPlaybackMuted, initiatedBy } = mutingState
  const localParticipant = roomState.participants.find((p) => p.isLocal)

  if (!localParticipant?.tracks.cameraAudio) return

  const isAudioEnabled = localParticipant.tracks.cameraAudio.isEnabled

  if (isPlaybackMuted) {
    if (isAudioEnabled) {
      setTrackState('cameraAudio', false)
    }
  } else if (isMuted) {
    if (isAudioEnabled) {
      setTrackState('cameraAudio', false)

      if (initiatedBy !== localParticipant.id && initiatedBy !== 0) {
        showNotice({
          message: 'Your audio has been muted by the producer',
          expirationMillis: 3000,
        })
      }
    }
  } else {
    if (!isAudioEnabled) {
      setTrackState('cameraAudio', true)

      if (initiatedBy !== localParticipant.id && initiatedBy !== 0) {
        showNotice({
          message: 'Your audio has been unmuted by the producer',
          expirationMillis: 3000,
        })
      }
    }
  }
}

const syncPlaybackMuting = () => {
  const playbackMuteAllParticipant = (muted: boolean) => {
    // mute/unmute all participants
    roomState.participants.forEach((p) => {
      setMutingIntention({ participantId: p.id, isPlaybackMuted: muted })
    })
  }

  if (
    !isParticipantScreenAloneInFloor(0, floorState.floor, panelState, trayState)
  ) {
    playbackMuteAllParticipant(false)
    return
  }

  switch (playbackState.state?.state) {
    case 'producer_play_ready':
      if (
        isParticipantScreenInFloor(0, floorState.floor, panelState, trayState)
      ) {
        playbackMuteAllParticipant(true)
      }
      break
    case 'stop_intent_sent':
      playbackMuteAllParticipant(false)
      break
    // case 'pause_intent_sent': // TODO: mute if changing to another tray card
    // case 'resume_intent_sent':
    default:
  }
}

/// ////////////////////////////// ///
/// initialization and destruction ///
/// ////////////////////////////// ///

const subscriptions: (() => void)[] = []

const init = () => {
  if (!isHeadless) {
    // headless audio tracks should never be muted
    subscriptions.push(subscribe(mutingState, syncAudioStates))
    subscriptions.push(subscribe(roomState.participants, syncAudioStates))
    syncAudioStates()
  } else {
    // headless controls playback muting of participants
    subscriptions.push(subscribe(roomState.participants, syncPlaybackMuting))
    subscriptions.push(subscribeKey(playbackState, 'state', syncPlaybackMuting))
    subscriptions.push(subscribe(floorState, syncPlaybackMuting))
    subscriptions.push(subscribe(panelState, syncPlaybackMuting))
    syncPlaybackMuting()
  }
}

const destroy = () => {
  while (subscriptions.length > 0) {
    subscriptions.shift()?.()
  }
}

export { toggleParticipantAudio, enableParticipantAudio, init, destroy }
